<template>
    <div class="preset-runtime-value" :class="{ 'preset-runtime-value--simple': !showRuntimeControls, 'preset-runtime-value--multiline': multiline }">
        <label ref="valueContainer" class="preset-runtime-value__control" :class="{ 'preset-runtime-value__control--simple': !showRuntimeControls }">
            <span v-if="showRuntimeControls" class="mr-2">{{ $tt('固定值') }}</span>
            <div v-if="multiline" class="preset-runtime-value__input-wrap preset-runtime-value__input-wrap--textarea">
                <textarea
                    ref="valueControl"
                    :value="valueInput"
                    :disabled="showRuntimeControls && !!value"
                    class="textarea textarea-bordered textarea-sm w-full"
                    rows="8"
                    :placeholder="placeholder"
                    @input="handleValueInput"
                    @click="updateVariableDropdown($event.target)"
                    @keyup="updateVariableDropdown($event.target)"
                    @focus="updateVariableDropdown($event.target)"
                ></textarea>
                <button
                    class="btn btn-sm btn-outline preset-runtime-value__variable-toggle"
                    type="button"
                    :disabled="isValueControlDisabled"
                    :title="$tt('选择变量')"
                    :aria-label="$tt('选择变量')"
                    @mousedown.prevent
                    @click.stop="toggleVariableDropdown"
                >
                    $
                </button>
            </div>
            <div v-else class="preset-runtime-value__input-wrap">
                <input
                    ref="valueControl"
                    :value="valueInput"
                    :disabled="showRuntimeControls && !!value"
                    class="input input-bordered input-sm"
                    :placeholder="placeholder"
                    type="text"
                    @input="handleValueInput"
                    @click="updateVariableDropdown($event.target)"
                    @keyup="updateVariableDropdown($event.target)"
                    @focus="updateVariableDropdown($event.target)"
                />
                <button
                    class="btn btn-sm preset-runtime-value__variable-toggle"
                    type="button"
                    :disabled="isValueControlDisabled"
                    :title="$tt('选择变量')"
                    :aria-label="$tt('选择变量')"
                    @mousedown.prevent
                    @click.stop="toggleVariableDropdown"
                >
                    $
                </button>
            </div>
            <div
                v-if="showVariableDropdown"
                class="preset-runtime-value__variables"
                :class="{ 'preset-runtime-value__variables--up': dropdownDirection === 'up' }"
            >
                <button
                    v-for="item in normalizedVariableSuggestions"
                    :key="item.value"
                    class="preset-runtime-value__variable"
                    type="button"
                    @mousedown.prevent="selectVariable(item)"
                >
                    {{ item.label }}
                </button>
            </div>
        </label>
        <label v-if="showRuntimeControls" class="preset-runtime-value__control">
            <span class="mr-2">{{ $tt('取值方式') }}</span>
            <select :value="value" class="select select-bordered select-sm" @change="$emit('input', $event.target.value)">
                <option value="">{{ $tt('固定值') }}</option>
                <option v-for="field in inputSchema" :key="field.key" :value="field.key">
                    {{ $tt('执行时填写') }}：{{ field.label || field.key }}
                </option>
            </select>
        </label>
    </div>
</template>

<script>
import { MUSIC_VARIABLE_SUGGESTIONS } from '@/utils/musicVariables'

export default {
    name: 'RuntimeValue',
    props: {
        value: { type: String, default: '' },
        inputSchema: { type: Array, default: () => [] },
        valueInput: { type: [String, Number], default: '' },
        placeholder: { type: String, default: '' },
        showRuntimeControls: { type: Boolean, default: true },
        multiline: { type: Boolean, default: false },
        variableSuggestions: { type: Array, default: () => MUSIC_VARIABLE_SUGGESTIONS }
    },
    data() {
        return {
            variableTriggerActive: false,
            dropdownDirection: 'down',
            lastSelectionStart: 0,
            lastSelectionEnd: 0
        }
    },
    computed: {
        showVariableDropdown() {
            return !this.isValueControlDisabled && this.variableTriggerActive && this.normalizedVariableSuggestions.length > 0
        },
        isValueControlDisabled() {
            return this.showRuntimeControls && !!this.value
        },
        normalizedVariableSuggestions() {
            return this.variableSuggestions.map(item => {
                if (item && typeof item === 'object') return item
                return { label: item, value: item }
            })
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
    },
    methods: {
        handleValueInput(event) {
            this.$emit('value-input', event.target.value)
            this.updateVariableDropdown(event.target)
        },
        toggleVariableDropdown() {
            if (this.isValueControlDisabled) return
            this.syncSelectionFromControl()
            this.variableTriggerActive = !this.variableTriggerActive
            if (this.variableTriggerActive) {
                this.$nextTick(this.adjustDropdownPosition)
            }
        },
        updateVariableDropdown(target) {
            if (this.isValueControlDisabled || !target) {
                this.variableTriggerActive = false
                return
            }
            const value = target.value || ''
            const cursor = typeof target.selectionStart === 'number' ? target.selectionStart : value.length
            this.lastSelectionStart = cursor
            this.lastSelectionEnd = typeof target.selectionEnd === 'number' ? target.selectionEnd : cursor
            this.variableTriggerActive = value.slice(0, cursor).endsWith('$')
            if (this.variableTriggerActive) {
                this.$nextTick(this.adjustDropdownPosition)
            }
        },
        syncSelectionFromControl() {
            const control = this.$refs.valueControl
            const currentValue = this.valueInput === null || this.valueInput === undefined ? '' : String(this.valueInput)
            if (!control || document.activeElement !== control) {
                this.lastSelectionStart = currentValue.length
                this.lastSelectionEnd = currentValue.length
                return
            }
            this.lastSelectionStart = typeof control.selectionStart === 'number' ? control.selectionStart : currentValue.length
            this.lastSelectionEnd = typeof control.selectionEnd === 'number' ? control.selectionEnd : this.lastSelectionStart
        },
        adjustDropdownPosition() {
            const container = this.$refs.valueContainer
            if (!container) return
            const rect = container.getBoundingClientRect()
            const spaceBelow = window.innerHeight - rect.bottom
            const spaceAbove = rect.top
            this.dropdownDirection = spaceBelow < 260 && spaceAbove > spaceBelow ? 'up' : 'down'
        },
        selectVariable(variable) {
            const currentValue = this.valueInput === null || this.valueInput === undefined ? '' : String(this.valueInput)
            const start = Math.max(0, this.lastSelectionStart)
            const end = Math.max(start, this.lastSelectionEnd)
            const insertStart = start > 0 && currentValue[start - 1] === '$' ? start - 1 : start
            const variableValue = variable && variable.value ? variable.value : variable
            const nextValue = currentValue.slice(0, insertStart) + variableValue + currentValue.slice(end)
            const nextCursor = insertStart + variableValue.length
            this.$emit('value-input', nextValue)
            this.variableTriggerActive = false
            this.$nextTick(() => {
                const control = this.$refs.valueControl
                if (control && typeof control.setSelectionRange === 'function') {
                    control.focus()
                    control.setSelectionRange(nextCursor, nextCursor)
                }
            })
        },
        handleClickOutside(event) {
            const container = this.$refs.valueContainer
            if (container && !container.contains(event.target)) {
                this.variableTriggerActive = false
            }
        }
    }
}
</script>

<style scoped>
.preset-runtime-value {
    display: grid;
    gap: 8px;
    padding: 10px;
    border: 1px solid oklch(var(--bc) / .1);
    border-radius: 8px;
    background: oklch(var(--b2) / .3);
}

.preset-runtime-value--simple {
    padding: 0;
    border: 0;
    background: transparent;
}

.preset-runtime-value--multiline {
    align-items: stretch;
}

.preset-runtime-value__control {
    position: relative;
    display: grid;
    grid-template-columns: 58px minmax(0, 1fr);
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.preset-runtime-value__control--simple {
    grid-template-columns: minmax(0, 1fr);
}

.preset-runtime-value__control>span {
    color: oklch(var(--bc) / .68);
    font-size: 12px;
    font-weight: 650;
    line-height: 1.2;
    white-space: nowrap;
}

.preset-runtime-value__control .input,
.preset-runtime-value__control .select {
    width: 100%;
}

.preset-runtime-value__input-wrap {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 2.25rem;
    gap: 6px;
    min-width: 0;
}

.preset-runtime-value__input-wrap .input {
    min-width: 0;
}

.preset-runtime-value__variable-toggle {
    min-height: 2rem;
    width: 2.25rem;
    padding: 0;
}

.preset-runtime-value__variables {
    position: absolute;
    top: calc(100% + 4px);
    left: 66px;
    right: 0;
    z-index: 30;
    display: grid;
    max-height: 260px;
    overflow-y: auto;
    padding: 8px;
    border: 1px solid oklch(var(--bc) / .1);
    border-radius: 8px;
    background: oklch(var(--b1));
    box-shadow: 0 12px 28px oklch(var(--bc) / .14);
}

.preset-runtime-value__variables--up {
    top: auto;
    bottom: calc(100% + 4px);
}

.preset-runtime-value__control--simple .preset-runtime-value__variables {
    left: 0;
}

.preset-runtime-value__variable {
    min-height: 30px;
    padding: 6px 8px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: oklch(var(--bc));
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 12px;
    text-align: left;
    cursor: pointer;
}

.preset-runtime-value__variable:hover {
    background: oklch(var(--b2));
}

</style>
